<template>
  <div class="stance-grid">
    <position-selector
      :value="getStateValue('position')"
      :disabled="readonly"
      :use-default="isNewCase"
      data-testid="position-selector-container"
      @input="value => setStateValue('position', value)"
    />
    <state-input
      :state="animationStateWithEligibleValues"
      :initial-value="getStateValue('animation')"
      :synced-value="getStateValue('animation')"
      :disabled="readonly"
      :language="caseConf.language"
      :use-default="isNewCase"
      data-testid="behavior-container"
      @input="value => setStateValue('animation', value)"
    />
    <state-input
      :disabled="readonly || !shouldHaveAnimationDelay"
      :state="getStateById('animationDelay')"
      :initial-value="getStateValue('animationDelay')"
      :language="caseConf.language"
      :use-default="isNewCase"
      data-testid="repeats-every-container"
      @input="value => setStateValue('animationDelay', value)"
    />
  </div>
</template>

<script>
import { assign, get, set, debounce } from 'lodash';
import StateInput from '~lib/common/components/StateInput.vue';
import PositionSelector from '../../common/PositionSelector.vue';

export default {
  name: 'patient-stance',
  components: {
    StateInput,
    PositionSelector,
  },
  props: {
    states: {
      type: Array,
      default: () => [],
    },
    caseConf: {
      type: Object,
      default: () => ({
        language: 'en',
      }),
    },
    user: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isNewCase: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {
        caseConf: null,
      },
    };
  },
  computed: {
    defaults() {
      return {
        _initialStates: this.states.filter(s => s.category === 'stance').map(state => ({
          id: state.id,
          options: {
            value: this.getStateValue(state.id, this.caseConf),
          },
        })),
      };
    },
    shouldHaveAnimationDelay() {
      // Don't display the animation delay input if the
      // selected animation does not have a repeat delay
      if (!this.state.caseConf) {
        return true;
      }
      const animationState = this.getStateById('animation');
      if (!animationState) {
        return false;
      }
      const animationStateValue = this.getStateValue(animationState.id);
      if (!animationStateValue) {
        return false;
      }
      const animationEnumValue = animationState.possibleValues.find(
        v => v.key === animationStateValue,
      );
      if (!animationEnumValue.meta.hasRepeatDelay) {
        return false;
      }
      return true;
    },
    eligibleAnimationValues() {
      const animationState = this.getStateById('animation');
      return animationState.possibleValues.filter(
        value =>
          value.meta &&
          value.meta.isRepeating &&
          value.meta.compatiblePositions &&
          value.meta.compatiblePositions.includes(this.getStateValue('position')) &&
          (!value.meta.isMilitaryOnly || this.isMilitaryOrg || this.isMilitaryAnimation)
      );
    },
    animationStateWithEligibleValues() {
      return {
        ...this.getStateById('animation'),
        possibleValues: this.eligibleAnimationValues,
      };
    },
    isMilitaryOrg() {
      return this.user['https://vrpatients.com/productType'] === 'MILITARY';
    },
    isMilitaryAnimation() {
      const animationState = this.getStateById('animation');
      const value = this.getStateValue('animation');
      if (!animationState || !value) {
        return false;
      }
      const selectedValue = animationState.possibleValues.find(v => v.key === value);
      return selectedValue && selectedValue.meta.isMilitaryOnly;
    },
  },
  created() {
    this.state.caseConf = assign(this.state.caseConf, this.defaults);
    this.emitUpdate = debounce(this.emitUpdate);
  },
  methods: {
    getStateById(id) {
      return this.states.find(s => s.id === id);
    },
    updateCase(key, value) {
      set(this.state.caseConf, key, value);
      this.emitUpdate();
    },
    getStateValue(stateId, caseConf = this.state.caseConf) {
      if (!caseConf || !caseConf._initialStates) {
        return undefined;
      }
      return get(
        caseConf._initialStates.find(s => s.id === stateId),
        'options.value',
      );
    },
    setStateValue(stateId, value) {
      set(
        this.state.caseConf._initialStates.find(s => s.id === stateId),
        'options.value',
        value,
      );
      // Replace the animation if it's not compatible with the selected position
      if (stateId === 'position') {
        const eligibleAnimationEnumValues = this.eligibleAnimationValues.map(
          enumValue => enumValue.key,
        );
        if (
          !eligibleAnimationEnumValues.includes(this.getStateValue('animation'))
        ) {
          this.setStateValue('animation', 'IDLE');
        }
      }
      if (stateId === 'animation') {
        if (!this.shouldHaveAnimationDelay) {
          this.setStateValue('animationDelay', 0);
        }
      }
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('update', this.state.caseConf);
    },
  },
};
</script>

<style scoped>
.stance-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
}
.position-selector {
  grid-column-end: span 2;
}
.state-input label {
  text-transform: capitalize;
}
</style>
